import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Select, Stat } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { ServicePitch } from '@/components/ServicePitch';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Textarea } from '@/components/ui';
import { ConversationPreview } from '@/components/ConversationPreview';
import { answerConversationGap, deleteConversationFaq, getConversation, getConversationFaq, getConversationSessions, updateConversationFaq, type ConversationFaq, type ConversationPayload } from '@/data/client';

const RANGE_OPTS = [{ value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' }, { value: '90d', label: 'Last 90 days' }];
const INTENT_LABEL: Record<string, string> = { quote: 'Wanted a quote', scheduling: 'Wanted to schedule', comparing: 'Comparing options', browsing: 'Browsing', support: 'Existing customer', other: 'Other' };

/* The client's view of the website chat: how many conversations, how many
   became leads, what visitors wanted, and the questions it could not answer.
   Summaries only — the full transcript lives with the lead in leadIQ. */
export function ConversationIQ() {
  const [range, setRange] = useState('30d');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['conversation', range], queryFn: () => getConversation(range) });

  if (isLoading) return <div className="dash-stack"><Card padding="lg"><p className="dash-muted">Loading conversations…</p></Card></div>;
  const d = data as ConversationPayload | undefined;
  if (!d || !d.configured) {
    return (
      <ServicePitch
        icon="message"
        service="conversationIQ"
        headline="Turn website visitors into conversations, and conversations into leads."
        benefits={[
          'An AI assistant on your site, in your name, that answers from your own pages and FAQ and nothing else.',
          'Visitors get real answers at 11 pm; the ones who want a quote leave a name and number without filling a form.',
          'Every lead lands in leadIQ with the conversation attached and an email to your sales inbox.',
          'The questions it could not answer become the next FAQ entries and article topics.',
        ]}
        roles={['Account Manager', 'Design / Dev Manager', 'Brand / Strategy Manager']}
        ask="about conversationIQ and putting an AI assistant on your website."
        adminHint={<>Admin: set it up under this client&rsquo;s <strong>conversationIQ</strong> tab and add the loader script to their site.</>}
        aside={<ConversationPreview />}
      />
    );
  }
  const maxDay = Math.max(1, ...d.byDay.map((x) => x.sessions));
  const intentTotal = d.byIntent.reduce((n, i) => n + i.count, 0) || 1;

  return (
    <div className="dash-stack">
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field"><Icon name="calendar" size={15} /><Select options={RANGE_OPTS} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range" /></label>
        </div>
        <div className="lead-toolbar__right">{d.enabled ? <Badge variant="success" dot>Live on your site</Badge> : <Badge variant="neutral">Chat is off</Badge>}</div>
      </div>

      <div className="lead-mini lead-mini--4">
        <Card padding="lg" className="lead-mini__card"><Stat label="Conversations" value={d.totals.sessions} sub="in selected range" info="Website visitors who exchanged at least one message with the assistant." /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Leads captured" value={d.totals.leads} sub={`${d.totals.leadRate}% of conversations`} info="Conversations where the visitor left a name and a phone or email. Each is in leadIQ with the transcript." /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Messages per chat" value={d.totals.avgMessages} sub="average" /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Unanswered questions" value={d.totals.unanswered} sub="worth adding to the FAQ" info="Questions the assistant could not answer from your website and FAQ. The list is below." /></Card>
      </div>

      <div className="lead-mini lead-mini--2">
        <Card padding="lg">
          <h2 className="dash-card__title">Conversations by day</h2>
          {d.byDay.length === 0 ? <p className="dash-muted">Nothing yet.</p> : (
            <div className="conv-bars" role="img" aria-label="Conversations per day">
              {d.byDay.map((x) => (
                <div key={x.day} className="conv-bar" title={`${x.day}: ${x.sessions} conversation${x.sessions === 1 ? '' : 's'}, ${x.leads} lead${x.leads === 1 ? '' : 's'}`}>
                  <div className="conv-bar__fill" style={{ height: `${Math.round((x.sessions / maxDay) * 100)}%` }}>{x.leads > 0 && <div className="conv-bar__lead" style={{ height: `${Math.round((x.leads / x.sessions) * 100)}%` }} />}</div>
                </div>
              ))}
            </div>
          )}
          <p className="dash-muted" style={{ fontSize: 'var(--text-xs)', marginTop: 8 }}>Darker portion: conversations that became leads.</p>
        </Card>
        <Card padding="lg">
          <h2 className="dash-card__title">What visitors wanted</h2>
          {d.byIntent.length === 0 ? <p className="dash-muted">Nothing yet.</p> : (
            <ul className="conv-intents">
              {d.byIntent.map((i) => (
                <li key={i.intent}>
                  <span>{INTENT_LABEL[i.intent] ?? i.intent}</span>
                  <span className="conv-intents__bar"><span style={{ width: `${Math.round((i.count / intentTotal) * 100)}%` }} /></span>
                  <span className="dash-muted">{i.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {d.gaps.length > 0 && (
        <Card padding="lg">
          <h2 className="dash-card__title">Questions we couldn&rsquo;t answer</h2>
          <p className="dash-muted" style={{ marginTop: 0 }}>The assistant only answers from your website and FAQ. Answer one here and it goes into the FAQ the assistant uses, within a minute.</p>
          <ul className="conv-gaps">{d.gaps.map((g) => <GapRow key={g.question} question={g.question} count={g.count} onAnswered={() => qc.invalidateQueries({ queryKey: ['conversation'] })} />)}</ul>
        </Card>
      )}

      <FaqManager onChanged={() => qc.invalidateQueries({ queryKey: ['conversation'] })} />

      <RecentConversations range={range} />
    </div>
  );
}

function GapRow({ question, count, onAnswered }: { question: string; count: number; onAnswered: () => void }) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await answerConversationGap({ question, answer }); setMsg('Added to the FAQ.'); setOpen(false); onAnswered(); }
    catch (e) { setMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Could not save.'); }
    finally { setBusy(false); }
  };
  return (
    <li className={open ? 'is-open' : ''}>
      <div className="conv-gaps__row">
        <span>{question}</span>
        <span className="conv-gaps__meta"><span className="dash-muted">asked {count}×</span>{!open && <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Answer</Button>}</span>
      </div>
      {open && (
        <div className="conv-gaps__answer">
          <textarea className="asst__input" rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Write the answer the way you would say it to a customer." autoFocus />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button size="sm" disabled={busy || !answer.trim()} onClick={save}>{busy ? 'Saving…' : 'Save to FAQ'}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
            {msg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</span>}
          </div>
        </div>
      )}
      {!open && msg && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</div>}
    </li>
  );
}

/* The client's FAQ: what the assistant answers from first. Same table Triton
   edits in Admin; a change rebuilds the corpus on the next conversation. */
function FaqManager({ onChanged }: { onChanged: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['conversation', 'faq'], queryFn: getConversationFaq });
  const [q, setQ] = useState(''); const [a, setA] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<ConversationFaq | null>(null);
  const [adding, setAdding] = useState(false);
  const items = data?.faq ?? [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ['conversation', 'faq'] }); onChanged(); };
  const add = async () => {
    setBusy(true); setMsg(null);
    try { await answerConversationGap({ question: q, answer: a }); setQ(''); setA(''); setAdding(false); refresh(); }
    catch (e) { setMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Could not add.'); } finally { setBusy(false); }
  };
  return (
    <Card padding="lg">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 className="dash-card__title" style={{ marginBottom: 2 }}>Questions &amp; answers</h2>
          <p className="dash-muted" style={{ margin: 0 }}>What the assistant answers from first, in your words. Add the questions customers ask; edit anything it should say differently.</p>
        </div>
        {!adding && <Button size="sm" onClick={() => setAdding(true)} iconLeft={<Icon name="plus" size={14} />}>Add a question</Button>}
      </div>
      {adding && (
        <div className="conv-faq__form">
          <Input label="Question" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Do you offer financing?" autoFocus />
          <label className="tr-field"><span className="tr-field__label">Answer</span><Textarea rows={3} value={a} onChange={(e) => setA(e.target.value)} placeholder="The answer, the way you would say it to a customer." /></label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button size="sm" disabled={busy || !q.trim() || !a.trim()} onClick={add}>{busy ? 'Saving…' : 'Save'}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setAdding(false); setMsg(null); }}>Cancel</Button>
            {msg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>{msg}</span>}
          </div>
        </div>
      )}
      {isLoading ? <p className="dash-muted">Loading…</p> : items.length === 0 ? (
        !adding && <p className="dash-muted" style={{ marginTop: 12 }}>No questions yet. The assistant is answering from your website pages alone.</p>
      ) : (
        <div className="admin-chat__faq">
          {items.map((k) => (
            <div key={k.id} className={`admin-chat__faqitem ${k.enabled ? '' : 'is-off'}`}>
              {editing?.id === k.id ? (
                <div className="conv-faq__form" style={{ width: '100%', marginTop: 0 }}>
                  <Input label="Question" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} />
                  <label className="tr-field"><span className="tr-field__label">Answer</span><Textarea rows={3} value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} /></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" onClick={async () => { await updateConversationFaq(k.id, { question: editing.question, answer: editing.answer, enabled: editing.enabled }); setEditing(null); refresh(); }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-chat__faqtext"><strong>{k.question}</strong><span>{k.answer}</span></div>
                  <div className="admin-chat__faqactions">
                    <button type="button" onClick={() => setEditing(k)} title="Edit"><Icon name="edit" size={14} /></button>
                    <button type="button" onClick={async () => { await updateConversationFaq(k.id, { question: k.question, answer: k.answer, enabled: !k.enabled }); refresh(); }} title={k.enabled ? 'Pause — keep it but stop using it' : 'Use again'}><Icon name={k.enabled ? 'eye' : 'eye-off'} size={14} /></button>
                    <button type="button" onClick={async () => { if (window.confirm('Remove this question and answer?')) { await deleteConversationFaq(k.id); refresh(); } }} title="Remove"><Icon name="trash" size={14} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const PAGE_SIZE = 10;
function RecentConversations({ range }: { range: string }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [range]);
  const { data, isLoading, isFetching } = useQuery({ queryKey: ['conversation', 'sessions', range, page], queryFn: () => getConversationSessions(range, page, PAGE_SIZE), placeholderData: (prev) => prev });
  const rows = data?.sessions ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <Card padding="lg">
      <h2 className="dash-card__title">Recent conversations</h2>
      {isLoading ? <p className="dash-muted">Loading…</p> : rows.length === 0 ? <p className="dash-muted">No conversations in this range.</p> : (
        <div style={{ overflowX: 'auto', opacity: isFetching ? 0.6 : 1 }}>
          <table className="dash-table">
            <thead><tr><th>When</th><th>Summary</th><th>Wanted</th><th>Outcome</th><th>Source</th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="dash-muted" style={{ whiteSpace: 'nowrap' }}>{s.startedAt.slice(0, 16).replace('T', ' ')}</td>
                  <td>{s.summary}</td>
                  <td className="dash-muted">{INTENT_LABEL[s.intent] ?? s.intent}</td>
                  <td>{s.status === 'lead' ? <Badge variant="success" dot>Lead{s.leadName ? `: ${s.leadName}` : ''}</Badge> : s.status === 'open' ? <Badge variant="neutral">In progress</Badge> : <span className="dash-muted">No contact left</span>}</td>
                  <td className="dash-muted">{s.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <div className="conv-pager">
          <span className="dash-muted">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" variant="outline" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= pages || isFetching} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
      <p className="dash-muted" style={{ fontSize: 'var(--text-xs)', marginTop: 10 }}>Summaries are kept for good. Full transcripts are kept only for conversations that became leads, in leadIQ.</p>
    </Card>
  );
}
